import React from "react";
import { useHistory, useParams } from "react-router-dom";
import CallGraphSection from "./CallGraphSection";
import DescriptionSection from "./DescriptionSection";
import API from "@/api/serverCall";
import { applicationDetailInfoType } from "types/app";
import LinkSection from "@/views/Application/Dashboard/LinkSection";
import ChartSection from "./ChartSection";
import { numberWithCommas } from "@/utils/number";
import CountUpIcon from "@/assets/count_up.svg";
import CountDownIcon from "@/assets/count_down.svg";
import {
	FormControl,
	FormHelperText,
	MenuItem,
	Select,
	SelectChangeEvent,
} from "@mui/material";
import { format } from "date-fns";
import { getApplicationInfo } from "@/redux/organization/selector";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

export interface IDailyCount {
	date: string;
	count: number;
}

export interface DashboardDetailCtlDto {
	body: {
		id: number;
		name: string;
		planName: string;
		packageIos: string;
		packageAndroid: string;
		nextPaymentAt: string;
		createdAt: string;
		expiredAt: string;
		isUse: boolean;
		planCount: {
			startAt: string;
			endAt: string;
			count: number;
			maxCount: number;
		};
		monthCount: {
			startAt: string;
			endAt: string;
			count: number;
			maxCount: number;
		};
		dailyCount: IDailyCount[];
	};
	success: boolean;
	code: string;
}

const initApplicationDetailInfo = {
	app: {
		appToken: "",
		companyToken: "",
		createdAt: "",
		id: 0,
		name: "",
		nextPaymentAt: "",
		packageAndroid: "",
		packageIos: "",
	},
	currentCount: 0,
	totalCount: 0,
	updatedAt: "",
};

const initDashboardDetailInfo = {
	body: {
		id: 0,
		name: "",
		planName: "_",
		packageIos: "",
		packageAndroid: "",
		nextPaymentAt: "",
		createdAt: "",
		expiredAt: "",
		isUse: false,
		planCount: {
			startAt: "",
			endAt: "",
			count: 0,
			maxCount: 0,
		},
		monthCount: {
			startAt: "",
			endAt: "",
			count: 0,
			maxCount: 0,
		},
		dailyCount: [
			{
				date: "",
				count: 0,
			},
			{
				date: "",
				count: 0,
			},
		],
	},
	success: true,
	code: "",
};

const ApplicationDashboard = () => {
	const { id } = useParams<{ id?: string }>();
	const history = useHistory();
	const { t } = useTranslation();
	const applicationList = useSelector(getApplicationInfo);
	const [ApplicationDetailInfo, setApplicationDetailInfo] =
		React.useState<applicationDetailInfoType>(initApplicationDetailInfo);
	const [DashboardDetailInfo, setDashboardDetailInfo] = React.useState(
		initDashboardDetailInfo,
	);
	const [lastDays, setLastDays] = React.useState("30");
	const today = new Date();
	const pastDay = new Date();

	const getApplicationDetailInfo = (id: string) => {
		// loading
		try {
			API.GET<applicationDetailInfoType>({
				link: `/app/${id}`,
			}).then((response) => {
				setApplicationDetailInfo(response.data);
			});
		} catch (error) {
			console.error(error);
		}
		/* try {
			API.GET<applicationDetailInfoType>({
				link: `/app/${id}`,
			}).then((response) => {
				console.log(response);
				setApplicationDetailInfo(response.data);
			});
		} catch (error) {
			console.error(error);
		} */
	};

	const getDashboardInfo = (id: string) => {
		// loading
		try {
			API.GET<DashboardDetailCtlDto>({
				link: `/dashboard/${id}`,
			}).then((response) => {
				setDashboardDetailInfo(response.data);
			});
		} catch (error) {
			console.error(error);
		}
	};

	React.useEffect(() => {
		if (id) {
			getApplicationDetailInfo(id);
			getDashboardInfo(id);
		}
	}, [id]);

	const handleApplicationChange = (e: SelectChangeEvent) => {
		history.push(`/application/${e.target.value}/dashboard`);
	};

	const handleLastDaysChange = (e: SelectChangeEvent) => {
		setLastDays(e.target.value);
	};

	return (
		<div style={{ flexGrow: 1, padding: "58px 60px" }}>
			{DashboardDetailInfo.body.planName === "" && (
				<section style={{ marginBottom: "35px" }}>
					<div
						style={{
							display: "flex",
							gap: "10px",
							backgroundColor: "#fff9e6",
							borderRadius: "4px",
							padding: "21px 45px",
							alignItems: "center",
						}}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="24"
							height="24"
							viewBox="0 0 24 24"
							fill="none"
						>
							<g clipPath="url(#clip0_302_5678)">
								<path
									d="M15.73 3H8.27L3 8.27V15.73L8.27 21H15.73L21 15.73V8.27L15.73 3ZM12 17.3C11.28 17.3 10.7 16.72 10.7 16C10.7 15.28 11.28 14.7 12 14.7C12.72 14.7 13.3 15.28 13.3 16C13.3 16.72 12.72 17.3 12 17.3ZM13 13H11V7H13V13Z"
									fill="#E14400"
								/>
							</g>
							<defs>
								<clipPath id="clip0_302_5678">
									<rect width="24" height="24" fill="white" />
								</clipPath>
							</defs>
						</svg>
						<div>{t("application_detail.payment_guide")}</div>
						<button
							style={{
								backgroundColor: "#e14400",
								color: "white",
								padding: "8px 20px",
								border: "0px",
								borderRadius: "4px",
								cursor: "pointer",
								fontSize: "14px",
							}}
							onClick={() => {
								history.push("/price", {
									appId: id,
									appName: ApplicationDetailInfo.app.name,
									isUse: false,
								});
							}}
						>
							{t("application_detail.payment_guide_button")}
						</button>
					</div>
				</section>
			)}
			<section style={{ marginBottom: "1rem" }}>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "1rem",
					}}
				>
					<div style={{ fontSize: "1.5rem", fontWeight: 700 }}>
						Application List
					</div>
					<FormControl>
						<Select value={id} onChange={handleApplicationChange}>
							{applicationList?.map((app) => {
								return <MenuItem value={app.id}>{app.name}</MenuItem>;
							})}
						</Select>
					</FormControl>
				</div>
			</section>
			<section>
				<DescriptionSection
					dashboardObject={DashboardDetailInfo.body}
					translationObject={t}
				/>
			</section>
			<section>
				<LinkSection translationObject={t} />
			</section>
			<section>
				<CallGraphSection
					planCount={DashboardDetailInfo.body.planCount}
					monthCount={DashboardDetailInfo.body.monthCount}
					getDashboardInfo={getDashboardInfo}
				/>
			</section>
			<section>
				<div style={{ height: "400px" }}>
					<div
						style={{
							fontSize: "1.25rem",
							fontWeight: 700,
							marginBottom: "1rem",
						}}
					>
						일일 사용량
					</div>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
						}}
					>
						<div style={{ display: "flex", gap: "0.5rem" }}>
							<span style={{ fontSize: "2.5rem" }}>
								{numberWithCommas(
									DashboardDetailInfo.body.dailyCount[0].count,
								)}
							</span>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: "0.3rem",
									fontWeight: 700,
									color: `${
										DashboardDetailInfo.body.dailyCount[0].count -
											DashboardDetailInfo.body.dailyCount[1].count <
										0
											? "#696969"
											: "#2ba640"
									}`,
								}}
							>
								<img
									src={`${
										DashboardDetailInfo.body.dailyCount[0].count -
											DashboardDetailInfo.body.dailyCount[1].count <
										0
											? CountDownIcon
											: CountUpIcon
									}`}
									alt="count_up_icon"
								/>
								{numberWithCommas(
									DashboardDetailInfo.body.dailyCount[0].count -
										DashboardDetailInfo.body.dailyCount[1].count,
								)}{" "}
								{DashboardDetailInfo.body.dailyCount[1].count === 0
									? `(+${numberWithCommas(
											DashboardDetailInfo.body.dailyCount[0].count,
									  )})`
									: `(${Math.floor(
											((DashboardDetailInfo.body.dailyCount[0]
												.count -
												DashboardDetailInfo.body.dailyCount[1]
													.count) /
												DashboardDetailInfo.body.dailyCount[1]
													.count) *
												100,
									  )}%)`}
							</div>
						</div>
						<div>
							<FormControl sx={{ m: 1, minWidth: 120 }}>
								<Select value={lastDays} onChange={handleLastDaysChange}>
									<MenuItem value={7}>
										<em>지난 7일</em>
									</MenuItem>
									<MenuItem value={30}>지난 30일</MenuItem>
									<MenuItem value={60}>지난 60일</MenuItem>
									<MenuItem value={90}>지난 90일</MenuItem>
								</Select>
								<FormHelperText>
									{format(
										new Date(
											pastDay.setDate(
												pastDay.getDate() - Number(lastDays) + 1,
											),
										),
										"yyyy-MM-dd",
									)}{" "}
									~ {format(today, "yyyy-MM-dd")}
								</FormHelperText>
							</FormControl>
						</div>
					</div>
					<ChartSection
						dailyCount={DashboardDetailInfo.body.dailyCount}
						selectedDailyNumber={lastDays}
					/>
				</div>
			</section>
		</div>
	);
};

export default ApplicationDashboard;
